MxNavigationService.$inject = [
	'$window',
	'mx.shell.NavigationProviderService',
	'mx.shell.RoutingService',
	'mx.shell.EventService',
	'mx.shell.UserProfileService',
	'mx.SolutionBuilderAgent.PersonalizationService',
	'mx.shell.AppsService',
];

/**
 * @ngdoc service
 * @name mx.shell.NavigationService
 * @module mx.shell
 *
 * @requires {@ref mx.shell.NavigationProviderService mx.shell.NavigationProviderService}
 * @requires {@ref mx.shell.RoutingService mx.shell.RoutingService}
 * @requires {@ref mx.shell.EventService mx.shell.EventService}
 *
 * @property {boolean} configurationModeOn - Gets or sets navigation configuration mode.
 *
 * @description
 * A service prepares navigation items and provides method to get them
 */
export function MxNavigationService($window, navigationProviderService, routingService, eventService, userProfileService, personalizationService, appsService) {
	const selfContract = {
		get,
		getNavigationItem,
		loadChildren,
		markFilterTextMatched,
		setNavigationItemById,
		setCurrentNavigationItem,
		setActiveStatus,
		getFlatArrayOfNavItems,
		setExtraItem,
		changeNavigationOrdering,
		setNavigationProvider,
	};

	eventService.subscribeGlobal(mx.shell.EventCodes.RouteActivated, setNavigationItemFromRoute);
	eventService.subscribeGlobal(mx.shell.EventCodes.NavigationChildrenLoaded, virtualCollectionChildrenLoaded);
	eventService.subscribeGlobal(mx.shell.EventCodes.NavigationReload, get.bind(this, true));

	let navItems;
	let navigationLoaderPromise;
	let partiallyOpenedItem;
	let currentItem;
	let extraItem;
	let configurationModeOn = false;
	selfContract.openSettingHandler = null;

	Object.defineProperty(selfContract, 'currentItem', {
		enumerable: true,
		get() {
			return currentItem;
		},
		set: setCurrentNavigationItem,
	});

	Object.defineProperty(selfContract, 'extraItem', {
		enumerable: true,
		get() {
			return extraItem;
		},
		set: setExtraItem,
	});

	Object.defineProperty(selfContract, 'navItems', {
		enumerable: true,
		get() {
			return navItems;
		},
	});

	Object.defineProperty(selfContract, 'configurationModeOn', {
		enumerable: true,
		get() {
			return configurationModeOn;
		},
		set(newValue) {
			if (configurationModeOn !== newValue) {
				configurationModeOn = newValue;
				eventService.dispatchEvent(mx.shell.EventCodes.NavigationConfigurationModeChanged, configurationModeOn);
			}
		},
	});

	return selfContract;

	function setNavigationProvider(navigationProvider) {
		if (navigationProvider && typeof navigationProvider.load === 'function') {
			navigationProviderService = navigationProvider;
		}
	}

	async function changeNavigationOrdering(movedItemName, receiverItem) {
		const movedItem = await getNavigationItemByName(movedItemName);

		// If moved and received items are children of the same parent
		// And in the same group (or both without group)
		// Then moving can be done.
		if (!movedItem || movedItem.parent !== receiverItem.parent || receiverItem.groupId !== movedItem.groupId || movedItem.parameters?.filterType !== 'filter') {
			return;
		}

		const items = movedItem.parent.children;

		const receiverIndex = items.indexOf(receiverItem);
		const movedItemIndex = items.indexOf(movedItem);

		items.splice(movedItemIndex, 1);
		items.splice(receiverIndex + (receiverIndex < movedItemIndex ? 1 : 0), 0, movedItem);

		const navigationItem = getNavigationItemOfFilter(movedItem);

		await personalizationService.saveNavigationFiltersOrdering(navigationItem, movedItem.parent);
	}

	function getNavigationItemOfFilter(filterNavigationItem) {
		let navigationItem = filterNavigationItem;
		do {
			navigationItem = navigationItem.parent;
		} while (navigationItem.__fromFilter);
		return navigationItem;
	}

	async function setNavigationItemFromRoute() {
		let item;

		const currentState = routingService.getCurrent();
		if (currentState.parameters && currentState.parameters.navigationId && currentState.parameters.navigationId.includes('_')) {
			item = await getNavigationItem(currentState.parameters.navigationId);
			if (!routingService.isCurrent((item.routingItem || {}).name, item.parameters)) {
				item = null;
			}
		} else {
			const items = await get();
			item = findCurrent(items);
		}

		if (item !== currentItem) {
			selfContract.currentItem = item;
		}
	}

	async function virtualCollectionChildrenLoaded(event, data) {
		const items = await selfContract.get(false);
		const parent = data.parent;
		const children = data.children;

		const parentItem = findByRouteParams(items, parent);

		if (parentItem) {
			children.forEach((child) => {
				child.parent = parent;
			});
			parentItem.children = children;

			let parentRoot = parentItem;
			let loop = true;

			while (loop) {
				const _parent = parentRoot.parent;

				if (!_parent || !_parent.childrenDynamicLoader) {
					loop = false;
				} else {
					parentRoot = _parent;
				}
			}
		}
	}

	function findByRouteParams(items, selectedItem) {
		let found = null;

		items.some((item) => {
			const isEqual = equalForParams(item.parameters, selectedItem.parameters);

			if (isEqual) {
				found = item;
				return true;
			} else if (item.children && item.children.length) {
				found = findByRouteParams(item.children, selectedItem);
				if (found) {
					return true;
				}
			}
		});

		return found;
	}

	function equalForParams(a, b) {
		if (!a || !b) {
			return false;
		}

		const keys = Object.keys(a);

		const length = keys.length;

		for (let i = 0; i < length; i++) {
			const k = keys[i];

			if (a[k] !== b[k]) {
				return false;
			}
		}

		return true;
	}

	async function loadChildren(item) {
		if (item.childrenDynamicLoader) {
			return await item.childrenDynamicLoader(item);
		} else {
			return item.children || [];
		}
	}

	async function findDynamicChild(children, parenId, ids) {
		if (Array.isArray(children) && Array.isArray(ids) && ids.length > 0) {
			const found = getItemById(children, `${ids[ids.length - 1]}_${parenId}`);
			if (!found) {
				return null;
			}

			if (ids.length > 1) {
				const children2 = await loadChildren(found);
				return await findDynamicChild(children2, found.id, ids.splice(0, ids.length - 1));
			} else {
				return found;
			}
		}
		return null;
	}

	function findCurrent(items) {
		const comparer = (item) => routingService.isCurrent((item.routingItem || {}).name, item.parameters);
		return getItem(items, comparer);
	}

	async function getNavigationItem(id) {
		const items = await get();
		return await getNavigationItemById(items, id);
	}

	async function getNavigationItemById(items = [], id) {
		if (id.includes('_')) {
			const ids = id.split('_');
			const parent = getItemById(items, ids[ids.length - 1]);
			let children = await loadChildren(parent);
			return findDynamicChild(children, parent.id, ids.splice(0, ids.length - 1));
		} else {
			return getItemById(items, id);
		}
	}

	async function getNavigationItemByName(name) {
		const items = await get();
		const comparer = (item) => item.name.toLowerCase() === name.toLowerCase();
		return getItem(items, comparer);
	}

	function getItemById(items, id) {
		const comparer = (item) => item.id.toLowerCase() === id.toLowerCase();
		return getItem(items, comparer);
	}

	function getItem(items, comparer) {
		let found = null;

		items.some((item) => {
			if (comparer(item)) {
				found = item;
				return true;
			} else if (Array.isArray(item.children)) {
				found = getItem(item.children, comparer);
				if (found) {
					return true;
				}
			}
		});

		return found;
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.NavigationService#get
	 *
	 * @param {boolean} reload - If true - forces navigation items to be loaded from {@ref mx.shell.NavigationProviderService navigationProviderService}.
	 *
	 * @description
	 * Gets navigation items from cache or loads it from {@ref mx.shell.NavigationProviderService navigationProviderService}.
	 *
	 * @returns {promise} Resolves as an array of {@ref mx.shell.NavigationItem mx.shell.NavigationItem}.
	 */
	function get(reload = false) {
		if (!navigationLoaderPromise || reload) {
			navigationLoaderPromise = loadNavigationItems();
		}
		return navigationLoaderPromise;
	}

	async function loadNavigationItems() {
		navItems = await navigationProviderService.load(true, true, navItems);
		navItems.forEach((item, i) => handleItem(item, i));
		const defaultRoute = await getDefaultRoute(navItems);
		eventService.dispatchEvent(mx.shell.EventCodes.NavigationLoaded, {
			navItems,
			routes: getRoutesFromNavigationItems(navItems),
			defaultRoute,
		});

		if (currentItem) {
			const newCurrentNavItem = await getNavigationItemById(navItems, currentItem.id);
			if (newCurrentNavItem) {
				await loadChildren(newCurrentNavItem);
				await setCurrentNavigationItem(newCurrentNavItem);
			} else {
				currentItem = undefined;
			}
		}
		return navItems;
	}

	function handleItem(navItem, index, parent) {
		// Set flag `hasChildren` if navigation-item already has an array of `children`
		// Or if those `children` can be loaded from server in future (`navItem.childrenDynamicLoader` is set)
		navItem.children = Array.isArray(navItem.children) ? navItem.children : [];
		navItem.hasChildren = Boolean((navItem.children && navItem.children.length) || navItem.childrenDynamicLoader);
		navItem.index = index;
		navItem.level = parent ? parent.level + 1 : 0;
		navItem.parent = parent;

		navItem.children.forEach((child, i) => {
			handleItem(child, i, navItem);
		});
	}

	function getRoutesFromNavigationItems(items) {
		const routes = getRoutesFromNavigationItemsRecursive(items);
		return Object.keys(routes).map((routeName) => routes[routeName]);
	}

	function getRoutesFromNavigationItemsRecursive(items) {
		const routes = {};
		items.forEach(({ routingItem, children }) => {
			if (routingItem && typeof routingItem === 'object') {
				const name = routingItem.name;
				routes[name] = routes[name] || routingItem;
			}
			const childrenRoutes = getRoutesFromNavigationItemsRecursive(children);
			Object.keys(childrenRoutes).forEach((childRouteName) => {
				routes[childRouteName] = routes[childRouteName] || childrenRoutes[childRouteName];
			});
		});

		return routes;
	}

	function getClosestRoutedNavItem(item) {
		if (!item) {
			return null;
		}

		if (item.routingItem) {
			return item;
		}

		if (item.children && item.children.length > 0) {
			return getClosestRoutedNavItem(item.children[0]);
		}

		return null;
	}

	/**
	 * @async
	 * @ngdoc method
	 * @name mx.shell.NavigationService#getDefaultNavigationItem
	 *
	 * @description
	 * Get Default Navigation Items.
	 *
	 * @returns {Promise<NavigationItem>}
	 */
	async function getDefaultNavigationItem(navItems) {
		const currentApp = await appsService.getCurrentApplication();
		const defaultApplicationNavigationItem = currentApp.navigationItem && (await getNavigationItemById(navItems, currentApp.navigationItem));

		const defaultNavigationItem = defaultApplicationNavigationItem || getClosestRoutedNavItem(navItems[0]);

		return (defaultNavigationItem && (await getDefaultFilterNavigationItem(defaultNavigationItem))) || defaultNavigationItem;
	}

	async function getDefaultFilterNavigationItem(item) {
		const defaultFilterId = await getDefaultFilterId(item);
		if (!defaultFilterId) {
			return null;
		}
		const filters = item.children.filter((child) => child.parameters?.filterType === 'filter');
		const comparer = (item) => item.parameters.filterId === defaultFilterId;
		return getItem(filters, comparer);
	}

	async function getDefaultRoute(navItems) {
		let item = await getDefaultNavigationItem(navItems);
		if (!item) {
			return;
		}

		return {
			name: item.routingItem?.name || '',
			parameters: item.parameters,
		};
	}

	async function getNavigateToItem(item) {
		const currentRouteParams = (routingService.getCurrent() || {}).parameters || {};
		const routingFromAnotherNavItem = currentRouteParams.navigationId !== item.id;
		return (routingFromAnotherNavItem && (await getDefaultFilterNavigationItem(item))) || item;
	}

	async function goToRoute(item) {
		if (/^https?:\/\//.test(item.route)) {
			$window.location.href = item.route;
		} else {
			const navigateToItem = await getNavigateToItem(item);

			await routingService.go(navigateToItem.route, { ...(navigateToItem.parameters || {}), extraFilter: '' });
		}
	}

	async function getDefaultFilterId(navItem) {
		let userStorage = await personalizationService.getDefaultFiltersForNavItem();
		const id = userStorage[navItem.id];
		if (id) {
			return id;
		}
		let user = await userProfileService.get();
		if (user && user.id) {
			return localStorage.getItem(`DefaultNavItemFilter${user.id}:${navItem.id}`) || navItem.defaultFilter;
		}
	}

	function setExtraItem(newExtraItem) {
		if (typeof newExtraItem !== 'string') {
			return;
		}

		setActiveStatus(partiallyOpenedItem, false);
		partiallyOpenedItem = undefined;
		setActiveStatus(currentItem, false);
		currentItem = null;
		extraItem = newExtraItem;

		eventService.dispatchEvent(mx.shell.EventCodes.CurrentNavigationItemChanged, {
			navigationItem: currentItem,
			extraItem,
			previousItem: currentItem,
		});
	}

	async function setNavigationItemById(navigationId) {
		let navigationItem = await getNavigationItem(navigationId);
		await setCurrentNavigationItem(navigationItem);
	}

	async function setCurrentNavigationItem(navigationItem) {
		if (!navigationItem || !navigationItem.id) {
			return;
		}
		if (navigationItem === currentItem) {
			setActiveStatus(currentItem, true);
			return;
		}

		navigationItem = await getNavigateToItem(navigationItem);

		const activatingPromise = selfContract.configurationModeOn ? Promise.resolve() : activateNavigationItem(navigationItem);
		const previousItem = currentItem;

		await activatingPromise;
		await loadChildren(navigationItem);
		setActiveStatus(partiallyOpenedItem, false);
		partiallyOpenedItem = undefined;
		if (navigationItem.route) {
			setActiveStatus(currentItem, false);
			currentItem = navigationItem;
			setActiveStatus(currentItem, true);

			eventService.dispatchEvent(mx.shell.EventCodes.CurrentNavigationItemChanged, {
				navigationItem: currentItem,
				previousItem,
			});
		} else if (!isAncestor(navigationItem)) {
			partiallyOpenedItem = navigationItem;
			setActiveStatus(navigationItem, true);
		}
	}

	function isAncestor(navItem) {
		let current = currentItem;
		while (current) {
			if (current === navItem) {
				return true;
			}
			current = current.parent;
		}
	}

	function setActiveStatus(navItem, state) {
		let item = navItem;
		while (item) {
			item.current = state;
			item.selected = state;
			item.parentOfActivated = false;
			item = item.parent;
		}

		if (navItem) {
			navItem.activated = state && !!navItem.route;

			if (navItem.activated && navItem.parent) {
				navItem.parent.parentOfActivated = true;
			}
		}
	}

	async function activateNavigationItem(item) {
		if (item && item.route && !routingService.isCurrent(item.route, item.parameters)) {
			if (item.openInNewWindow) {
				$window.open(item.route);
			} else {
				await goToRoute(item);
			}
		}
	}

	function markFilterTextMatched(navItems, filterText, showAllChildren, checkJustVisible) {
		let filterTextMatchedItem;
		let showAllChildrenForJustFoundItem;

		if (Array.isArray(navItems)) {
			navItems.forEach((item) => {
				if (!item._titleLowerCase) {
					item._titleLowerCase = item.title.toLowerCase();
				}

				item.selected = false;

				if ((!checkJustVisible || item.matchesFilterCriteria !== false) && filterText && item._titleLowerCase.includes(filterText)) {
					item.matchesFilterCriteria = true;
					filterTextMatchedItem = item;
					showAllChildrenForJustFoundItem = true;
				} else {
					item.matchesFilterCriteria = showAllChildren || !filterText;
				}

				markFilterTextMatched(item.children, filterText, showAllChildren || showAllChildrenForJustFoundItem);
				showAllChildrenForJustFoundItem = undefined;
			});
		}

		if (filterTextMatchedItem) {
			_expandAllParents(filterTextMatchedItem);
			filterTextMatchedItem = undefined;
		}
	}

	function _expandAllParents({ parent }) {
		if (parent) {
			parent.matchesFilterCriteria = true;
			parent.selected = true;
			_expandAllParents(parent);
		}
	}

	function getAllChildren(items = []) {
		let children = [];
		items.forEach((item) => {
			if (item.children) {
				children = children.concat(item.children);
			}
		});

		if (children.length) {
			children = children.concat(getAllChildren(children));
		}

		return children;
	}

	async function getFlatArrayOfNavItems() {
		let navItems1 = await selfContract.get();
		return await navItems1.concat(getAllChildren(navItems1));
	}
}
